import "./App.css";
import {
  ShoppingCart,
  Building2,
  Users,
  Menu,
  X,
  BarChart3,
  TrendingUp,
  LayoutDashboard,
  LogOut,
  Activity,
  LineChart,
  ClipboardCheck,
  Server,
  Search,
  Zap,
  Gauge,
  Package,
  Settings,
  FileText,
  Lock,
  CreditCard,
} from "lucide-react";

import React, { useEffect, useState } from "react";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import SiteManagement from "./components/SiteManagement";
import OrderAnalytics from "./components/OrderAnalytics";
import ProductAnalytics from "./components/ProductAnalytics";
import UserManagement from "./components/UserManagement";
import UptimeMonitoring from "./components/UptimeMonitoring";
import AnalyticsDashboard from "./components/AnalyticsDashboard";
import AuditDashboard from "./components/AuditDashboard";
import SiteSelector from "./components/SiteSelector";
import SystemInfo from "./components/SystemInfo";
import CheckoutAnalysis from "./components/CheckoutAnalysis";
import ContentInsights from "./components/ContentInsights";
import UserAnalysis from "./components/UserAnalysis";
import AISuggestions from "./components/AISuggestions";
import AdminSuggestions from "./components/AdminSuggestions";
import AdminUsers from "./components/AdminUsers";
import PageSpeed from "./components/PageSpeed";
import StockStatus from "./components/StockStatus";
import StoreConfig from "./components/StoreConfig";
import ProductQuality from "./components/ProductQuality";
import GoogleAnalytics from "./components/GoogleAnalytics";
import SearchConsole from "./components/SearchConsole";
import AbandonedCarts from "./components/AbandonedCarts";
import SSLMonitor from "./components/SSLMonitor";
import PlanManagement from "./components/PlanManagement";
import Paywall from "./components/Paywall";
import Reports from "./components/Reports";
import ThankYou from "./components/ThankYou";
import AppSettings from "./components/AppSettings";
import CloseAccount from "./components/CloseAccount";
import { SiteProvider } from "./contexts/SiteContext";

// Feature names for display in paywall
const FEATURE_NAMES = {
  orders: "Order Analytics",
  checkout: "Checkout Analysis",
  products: "Product Analytics",
  audit: "SEO Audit",
  "page-speed": "Page Speed",
  "product-quality": "Product Quality",
  content: "Content Insights",
  "ai-suggestions": "AI Suggestions",
  analytics: "Analytics Portal",
  "user-analysis": "User Analysis",
  uptime: "Uptime Monitoring",
  system: "System Information",
  "store-config": "Store Config",
  "stock-status": "Stock Status",
  "admin-users": "Admin Users",
  "google-analytics": "Google Analytics",
  "search-console": "Search Console",
  "abandoned-carts": "Abandoned Carts",
  "ssl-monitor": "SSL Monitor",
  "reports": "PDF Reports",
};

const AUTH_VIEWS = ['login', 'signup', 'thankyou', 'forgot'];

const App = () => {
  const [currentView, setCurrentView] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authView, setAuthView] = useState(() => {
    const hash = window.location.hash.slice(1);
    return AUTH_VIEWS.includes(hash) ? hash : 'login';
  });

  // Valid view keys
  const validViews = ['dashboard', 'sites', 'orders', 'products', 'uptime', 'analytics', 'audit', 'users', 'settings', 'system', 'checkout', 'content', 'user-analysis', 'ai-suggestions', 'admin-suggestions', 'admin-users', 'page-speed', 'stock-status', 'store-config', 'product-quality', 'plans', 'google-analytics', 'search-console', 'abandoned-carts', 'ssl-monitor', 'reports', 'close-account'];

  // Initialize current view from URL hash
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash && validViews.includes(hash)) {
      setCurrentView(hash);
    }
  }, []);

  // Listen for hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (AUTH_VIEWS.includes(hash)) {
        setAuthView(hash);
      } else if (hash && validViews.includes(hash)) {
        setCurrentView(hash);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Check for existing authentication
  useEffect(() => {
    const savedUser = localStorage.getItem("mageguard_user_v2");
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      setIsAuthenticated(true);
    }
  }, []);

  const setAuthHash = (view) => {
    setAuthView(view);
    window.location.hash = view;
  };

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem("mageguard_user_v2", JSON.stringify(userData));
    setCurrentView("dashboard");
    window.location.hash = "dashboard";
  };

  const handleLogout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem("mageguard_user_v2");
    setCurrentView("dashboard");
    setAuthHash("login");
  };

  // Check if user has access to a feature
  const hasFeatureAccess = (featureKey) => {
    // Admin has access to everything
    if (user?.role === 'admin') return true;
    // Check if user's plan includes the feature or has wildcard access
    const features = user?.plan?.features || [];
    return features.includes('*') || features.includes(featureKey);
  };

  // Navigation items based on user role
  const getNavigation = () => {
    // Global menu items (always visible)
    const globalNav = [
      {
        name: "Dashboard",
        key: "dashboard",
        icon: LayoutDashboard,
        current: currentView === "dashboard",
      },
      {
        name: "Site Management",
        key: "sites",
        icon: Building2,
        current: currentView === "sites",
      },
    ];

    // Sales & Orders group
    const salesNav = [
      {
        name: "Order Analytics",
        key: "orders",
        icon: ShoppingCart,
        current: currentView === "orders",
        locked: !hasFeatureAccess("orders"),
      },
      {
        name: "Checkout Analysis",
        key: "checkout",
        icon: ShoppingCart,
        current: currentView === "checkout",
        locked: !hasFeatureAccess("checkout"),
      },
      {
        name: "Abandoned Carts",
        key: "abandoned-carts",
        icon: ShoppingCart,
        current: currentView === "abandoned-carts",
        locked: !hasFeatureAccess("abandoned-carts"),
      },
      {
        name: "Product Analytics",
        key: "products",
        icon: TrendingUp,
        current: currentView === "products",
        locked: !hasFeatureAccess("products"),
      },
      {
        name: "Stock Status",
        key: "stock-status",
        icon: Package,
        current: currentView === "stock-status",
        locked: !hasFeatureAccess("stock-status"),
      },
    ];

    // SEO & Content group
    const seoNav = [
      {
        name: "SEO Audit",
        key: "audit",
        icon: ClipboardCheck,
        current: currentView === "audit",
        locked: !hasFeatureAccess("audit"),
      },
      {
        name: "Page Speed",
        key: "page-speed",
        icon: Gauge,
        current: currentView === "page-speed",
        locked: !hasFeatureAccess("page-speed"),
        // needsSetup: true,
      },
      {
        name: "Product Quality",
        key: "product-quality",
        icon: FileText,
        current: currentView === "product-quality",
        locked: !hasFeatureAccess("product-quality"),
      },
      {
        name: "Content Insights",
        key: "content",
        icon: Search,
        current: currentView === "content",
        locked: !hasFeatureAccess("content"),
      },
      {
        name: "AI Suggestions",
        key: "ai-suggestions",
        icon: Zap,
        current: currentView === "ai-suggestions",
        locked: !hasFeatureAccess("ai-suggestions"),
      },
    ];

    // Analytics group
    const analyticsNav = [
      {
        name: "Google Analytics",
        key: "google-analytics",
        icon: BarChart3,
        current: currentView === "google-analytics",
        locked: !hasFeatureAccess("google-analytics"),
        // needsSetup: true,
      },
      {
        name: "Search Console",
        key: "search-console",
        icon: Search,
        current: currentView === "search-console",
        locked: !hasFeatureAccess("search-console"),
        // needsSetup: true,
      },
      {
        name: "Page Views",
        key: "analytics",
        icon: LineChart,
        current: currentView === "analytics",
        locked: !hasFeatureAccess("analytics"),
      },
      {
        name: "User Analysis",
        key: "user-analysis",
        icon: Users,
        current: currentView === "user-analysis",
        locked: !hasFeatureAccess("user-analysis"),
      },
    ];

    // System group
    const systemNav = [
      {
        name: "PDF Reports",
        key: "reports",
        icon: FileText,
        current: currentView === "reports",
        locked: !hasFeatureAccess("reports"),
      },
      {
        name: "Uptime Monitoring",
        key: "uptime",
        icon: Activity,
        current: currentView === "uptime",
        locked: !hasFeatureAccess("uptime"),
      },
      {
        name: "SSL Monitor",
        key: "ssl-monitor",
        icon: Lock,
        current: currentView === "ssl-monitor",
        locked: !hasFeatureAccess("ssl-monitor"),
      },
      {
        name: "System Information",
        key: "system",
        icon: Server,
        current: currentView === "system",
        locked: !hasFeatureAccess("system"),
      },
      {
        name: "Store Config",
        key: "store-config",
        icon: Settings,
        current: currentView === "store-config",
        locked: !hasFeatureAccess("store-config"),
      },
      // {
      //   name: "Admin Users",
      //   key: "admin-users",
      //   icon: Users,
      //   current: currentView === "admin-users",
      //   locked: !hasFeatureAccess("admin-users"),
      // },
    ];

    // Admin-only menu items
    const adminNav = user?.role === 'admin' ? [
      {
        name: "User Management",
        key: "users",
        icon: Users,
        current: currentView === "users",
      },
      {
        name: "Plan Management",
        key: "plans",
        icon: CreditCard,
        current: currentView === "plans",
      },
      {
        name: "AI Suggestions",
        key: "admin-suggestions",
        icon: Zap,
        current: currentView === "admin-suggestions",
      },
      {
        name: "Settings",
        key: "settings",
        icon: Settings,
        current: currentView === "settings",
      },
    ] : [];

    // Account menu (site managers only)
    const accountNav = user?.role !== 'admin' ? [
      {
        name: "Close Account",
        key: "close-account",
        icon: Users,
        current: currentView === "close-account",
      },
    ] : [];

    return { globalNav, salesNav, seoNav, analyticsNav, systemNav, adminNav, accountNav };
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    setSidebarOpen(false);
    window.location.hash = view;
  };

  if (!isAuthenticated) {
    if (authView === 'thankyou') {
      return <ThankYou onBackToLogin={() => setAuthHash('login')} />;
    }
    return (
      <Login
        onLogin={handleLogin}
        onSignupSuccess={() => setAuthHash('thankyou')}
        initialMode={authView === 'signup' ? 'signup' : 'login'}
        onForgotPassword={() => setAuthHash('forgot')}
        onBackToLogin={() => setAuthHash('login')}
        onSwitchToSignup={() => setAuthHash('signup')}
        onSwitchToLogin={() => setAuthHash('login')}
      />
    );
  }

  const { globalNav, salesNav, seoNav, analyticsNav, systemNav, adminNav, accountNav } = getNavigation();

  return (
    <SiteProvider user={user}>
      <div className="h-screen bg-gray-100 flex">
        {/* Sidebar */}
        <div
          className={`${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } noprint fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}
        >
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-8 w-8 text-blue-600" />
              <span className="text-xl font-bold text-gray-900">MageGuard</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="mt-5 px-2 flex flex-col h-[calc(100%-6rem)] overflow-y-auto">
            {/* GLOBAL MENU */}
            <div className="space-y-1">
              {globalNav.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    onClick={() => handleViewChange(item.key)}
                    className={`${
                      item.current
                        ? "bg-blue-100 text-blue-900 border-r-2 border-blue-600"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    } group flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200`}
                  >
                    <Icon
                      className={`${
                        item.current
                          ? "text-blue-500"
                          : "text-gray-400 group-hover:text-gray-500"
                      } mr-3 h-5 w-5 flex-shrink-0`}
                    />
                    {item.name}
                  </button>
                );
              })}
            </div>

            {/* SALES SECTION */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Sales & Orders
              </p>
              <div className="space-y-1 pl-2">
                {salesNav.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      onClick={() => handleViewChange(item.key)}
                      className={`${
                        item.current
                          ? "bg-blue-100 text-blue-900 border-r-2 border-blue-600"
                          : item.locked
                          ? "text-gray-400 opacity-60"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      } group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200`}
                    >
                      <div className="flex items-center">
                        <Icon
                          className={`${
                            item.current
                              ? "text-blue-500"
                              : item.locked
                              ? "text-gray-300"
                              : "text-gray-400 group-hover:text-gray-500"
                          } mr-3 h-5 w-5 flex-shrink-0`}
                        />
                        {item.name}
                      </div>
                      {item.locked && (
                        <Lock className="h-3 w-3 text-gray-400" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* SEO & CONTENT SECTION */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                SEO & Content
              </p>
              <div className="space-y-1 pl-2">
                {seoNav.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      onClick={() => handleViewChange(item.key)}
                      className={`${
                        item.current
                          ? "bg-blue-100 text-blue-900 border-r-2 border-blue-600"
                          : item.locked
                          ? "text-gray-400 opacity-60"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      } group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon
                          className={`${
                            item.current
                              ? "text-blue-500"
                              : item.locked
                              ? "text-gray-300"
                              : "text-gray-400 group-hover:text-gray-500"
                          } h-5 w-5 flex-shrink-0`}
                        />
                        <span>{item.name}</span>
                        {item.needsSetup && !item.locked && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            Setup
                          </span>
                        )}
                      </div>
                      {item.locked && (
                        <Lock className="h-3 w-3 text-gray-400" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ANALYTICS SECTION */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Analytics
              </p>
              <div className="space-y-1 pl-2">
                {analyticsNav.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      onClick={() => handleViewChange(item.key)}
                      className={`${
                        item.current
                          ? "bg-blue-100 text-blue-900 border-r-2 border-blue-600"
                          : item.locked
                          ? "text-gray-400 opacity-60"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      } group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon
                          className={`${
                            item.current
                              ? "text-blue-500"
                              : item.locked
                              ? "text-gray-300"
                              : "text-gray-400 group-hover:text-gray-500"
                          } h-5 w-5 flex-shrink-0`}
                        />
                        <span className="text-left">{item.name}</span>
                        {item.needsSetup && !item.locked && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            Setup
                          </span>
                        )}
                      </div>
                      {item.locked && (
                        <Lock className="h-3 w-3 text-gray-400" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* SYSTEM SECTION */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                System
              </p>
              <div className="space-y-1 pl-2">
                {systemNav.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      onClick={() => handleViewChange(item.key)}
                      className={`${
                        item.current
                          ? "bg-blue-100 text-blue-900 border-r-2 border-blue-600"
                          : item.locked
                          ? "text-gray-400 opacity-60"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      } group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200`}
                    >
                      <div className="flex items-center">
                        <Icon
                          className={`${
                            item.current
                              ? "text-blue-500"
                              : item.locked
                              ? "text-gray-300"
                              : "text-gray-400 group-hover:text-gray-500"
                          } mr-3 h-5 w-5 flex-shrink-0`}
                        />
                        {item.name}
                      </div>
                      {item.locked && (
                        <Lock className="h-3 w-3 text-gray-400" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ADMIN SECTION */}
            {adminNav.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Administration
                </p>
                <div className="space-y-1">
                  {adminNav.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        onClick={() => handleViewChange(item.key)}
                        className={`${
                          item.current
                            ? "bg-blue-100 text-blue-900 border-r-2 border-blue-600"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        } group flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200`}
                      >
                        <Icon
                          className={`${
                            item.current
                              ? "text-blue-500"
                              : "text-gray-400 group-hover:text-gray-500"
                          } mr-3 h-5 w-5 flex-shrink-0`}
                        />
                        {item.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ACCOUNT SECTION (site managers) */}
            {accountNav.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Account
                </p>
                <div className="space-y-1">
                  {accountNav.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        onClick={() => handleViewChange(item.key)}
                        className={`${
                          item.current
                            ? "bg-blue-100 text-blue-900 border-r-2 border-blue-600"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        } group flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200`}
                      >
                        <Icon
                          className={`${
                            item.current
                              ? "text-blue-500"
                              : "text-gray-400 group-hover:text-gray-500"
                          } mr-3 h-5 w-5 flex-shrink-0`}
                        />
                        {item.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Spacer */}
            <div className="flex-1"></div>

            {/* User Info and Logout */}
            <div className="pt-4 border-t border-gray-200">
              <div className="px-3 space-y-2">
                <div className="px-2 py-2 text-sm text-gray-500">
                  <div className="font-medium text-gray-900">{user?.name}</div>
                  <div className="text-xs capitalize text-gray-500">{user?.role}</div>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-gray-600 hover:bg-gray-50 hover:text-gray-900 group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md"
                >
                  <LogOut className="text-gray-400 group-hover:text-gray-500 mr-3 h-5 w-5" />
                  Logout
                </button>
              </div>
            </div>
          </nav>
        </div>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden noprint"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top navigation bar */}
          <header className="bg-white shadow-sm border-b border-gray-200 noprint">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between h-16">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setSidebarOpen(true)}
                    className="text-gray-500 hover:text-gray-700 lg:hidden"
                  >
                    <Menu className="w-6 h-6" />
                  </button>
                  <div className="ml-4 flex items-center gap-2 lg:hidden">
                    <BarChart3 className="h-6 w-6 text-blue-600" />
                    <span className="text-lg font-semibold text-gray-900">MageGuard</span>
                  </div>
                  {['orders', 'products', 'uptime', 'analytics', 'audit', 'system', 'checkout', 'content', 'user-analysis', 'ai-suggestions', 'admin-users', 'page-speed', 'stock-status', 'store-config', 'product-quality', 'google-analytics', 'search-console', 'abandoned-carts', 'ssl-monitor', 'reports'].includes(currentView) && (
                    <div className="flex items-center">
                      <SiteSelector />
                    </div>
                  )}
                </div>
                <div className="flex items-center">
                  {/* Right side content placeholder */}
                </div>
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 overflow-auto">
            <div style={{ display: currentView === "dashboard" ? "block" : "none" }}>
              <Dashboard user={user} />
            </div>
            <div style={{ display: currentView === "sites" ? "block" : "none" }}>
              <SiteManagement user={user} />
            </div>
            <div style={{ display: currentView === "orders" ? "block" : "none" }}>
              {hasFeatureAccess("orders") ? <OrderAnalytics user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["orders"]} />}
            </div>
            <div style={{ display: currentView === "products" ? "block" : "none" }}>
              {hasFeatureAccess("products") ? <ProductAnalytics user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["products"]} />}
            </div>
            <div style={{ display: currentView === "uptime" ? "block" : "none" }}>
              {hasFeatureAccess("uptime") ? <UptimeMonitoring user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["uptime"]} />}
            </div>
            <div style={{ display: currentView === "analytics" ? "block" : "none" }}>
              {hasFeatureAccess("analytics") ? <AnalyticsDashboard user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["analytics"]} />}
            </div>
            <div style={{ display: currentView === "audit" ? "block" : "none" }}>
              {hasFeatureAccess("audit") ? <AuditDashboard user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["audit"]} />}
            </div>
            <div style={{ display: currentView === "system" ? "block" : "none" }}>
              {hasFeatureAccess("system") ? <SystemInfo user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["system"]} />}
            </div>
            <div style={{ display: currentView === "checkout" ? "block" : "none" }}>
              {hasFeatureAccess("checkout") ? <CheckoutAnalysis user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["checkout"]} />}
            </div>
            <div style={{ display: currentView === "content" ? "block" : "none" }}>
              {hasFeatureAccess("content") ? <ContentInsights user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["content"]} />}
            </div>
            <div style={{ display: currentView === "user-analysis" ? "block" : "none" }}>
              {hasFeatureAccess("user-analysis") ? <UserAnalysis user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["user-analysis"]} />}
            </div>
            <div style={{ display: currentView === "ai-suggestions" ? "block" : "none" }}>
              {hasFeatureAccess("ai-suggestions") ? <AISuggestions user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["ai-suggestions"]} />}
            </div>
            {user?.role === 'admin' && (
              <div style={{ display: currentView === "admin-suggestions" ? "block" : "none" }}>
                <AdminSuggestions user={user} />
              </div>
            )}
            <div style={{ display: currentView === "admin-users" ? "block" : "none" }}>
              {hasFeatureAccess("admin-users") ? <AdminUsers user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["admin-users"]} />}
            </div>
            {user?.role === 'admin' && (
              <div style={{ display: currentView === "users" ? "block" : "none" }}>
                <UserManagement user={user} />
              </div>
            )}
            {user?.role === 'admin' && (
              <div style={{ display: currentView === "plans" ? "block" : "none" }}>
                <PlanManagement user={user} />
              </div>
            )}
            {user?.role === 'admin' && (
              <div style={{ display: currentView === "settings" ? "block" : "none" }}>
                <AppSettings user={user} />
              </div>
            )}
            {user?.role !== 'admin' && (
              <div style={{ display: currentView === "close-account" ? "block" : "none" }}>
                <CloseAccount user={user} onLogout={handleLogout} />
              </div>
            )}
            <div style={{ display: currentView === "page-speed" ? "block" : "none" }}>
              {hasFeatureAccess("page-speed") ? <PageSpeed user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["page-speed"]} />}
            </div>
            <div style={{ display: currentView === "stock-status" ? "block" : "none" }}>
              {hasFeatureAccess("stock-status") ? <StockStatus user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["stock-status"]} />}
            </div>
            <div style={{ display: currentView === "store-config" ? "block" : "none" }}>
              {hasFeatureAccess("store-config") ? <StoreConfig user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["store-config"]} />}
            </div>
            <div style={{ display: currentView === "product-quality" ? "block" : "none" }}>
              {hasFeatureAccess("product-quality") ? <ProductQuality user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["product-quality"]} />}
            </div>
            <div style={{ display: currentView === "google-analytics" ? "block" : "none" }}>
              <GoogleAnalytics user={user} />
            </div>
            <div style={{ display: currentView === "search-console" ? "block" : "none" }}>
              {hasFeatureAccess("search-console") ? <SearchConsole user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["search-console"]} />}
            </div>
            <div style={{ display: currentView === "abandoned-carts" ? "block" : "none" }}>
              {hasFeatureAccess("abandoned-carts") ? <AbandonedCarts user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["abandoned-carts"]} />}
            </div>
            <div style={{ display: currentView === "ssl-monitor" ? "block" : "none" }}>
              {hasFeatureAccess("ssl-monitor") ? <SSLMonitor user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["ssl-monitor"]} />}
            </div>
            <div style={{ display: currentView === "reports" ? "block" : "none" }}>
              {hasFeatureAccess("reports") ? <Reports user={user} /> : <Paywall user={user} featureName={FEATURE_NAMES["reports"]} />}
            </div>
          </main>
        </div>
      </div>
    </SiteProvider>
  );
};

export default App;
